<template>
  <div class="chat-list-container">
    <div class="chat-list-header">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h5 class="mb-0">Conversas</h5>
        <div class="d-flex gap-2">
          <button
            class="btn btn-sm btn-primary"
            @click="$emit('new-chat')"
            title="Nova Conversa"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
          <div class="dropdown">
          <button
            class="btn btn-sm btn-icon btn-light dropdown-toggle"
            type="button"
            id="whatsappMenuDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title="Menu"
          >
            <i class="bi bi-three-dots-vertical"></i>
          </button>
          <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="whatsappMenuDropdown">
            <li>
              <router-link to="/whatsapp/templates" class="dropdown-item">
                <i class="bi bi-file-text me-2"></i>
                Templates
              </router-link>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="$emit('open-config')">
                <i class="bi bi-gear me-2"></i>
                Configurações
              </a>
            </li>
          </ul>
        </div>
        </div>
      </div>
      <div class="search-wrapper">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Buscar conversas..."
          class="search-input"
        />
        <i class="bi bi-search search-icon"></i>
      </div>
    </div>

    <div class="chat-list-content">
      <div
        v-for="chat in filteredChats"
        :key="chat.id"
        class="chat-item"
        :class="{ active: selectedChatId === chat.id }"
      >
        <div @click="selectChat(chat.id)" class="chat-item-content">
          <div class="chat-avatar" :class="{ 'has-photo': chat.user_photo }">
            <img v-if="chat.user_photo" :src="getPhotoUrl(chat.user_photo)" :alt="chat.user_name || 'Usuário'" />
            <i v-else class="bi bi-person"></i>
          </div>
          <div class="chat-info">
            <div class="chat-header-row">
              <span class="chat-name">{{ chat.user_name || formatPhone(chat.phone) }}</span>
              <span class="chat-time">{{ formatTime(chat.updated_at) }}</span>
            </div>
            <div class="chat-preview-row">
              <span class="chat-preview">{{ getPreview(chat.last_message) }}</span>
              <span v-if="chat.unread_count > 0" class="unread-badge">
                {{ chat.unread_count > 99 ? '99+' : chat.unread_count }}
              </span>
            </div>
            <div class="chat-status-row">
              <span
                class="status-badge"
                :class="chat.assigned_to === 'ai' ? 'ai-badge' : 'user-badge'"
              >
                {{ chat.assigned_to === 'ai' ? '🤖 IA' : '👤 Manual' }}
              </span>
            </div>
          </div>
        </div>
        <button
          class="chat-delete-btn"
          @click.stop="handleDelete(chat.id)"
          title="Deletar conversa"
        >
          <i class="bi bi-trash"></i>
        </button>
      </div>

      <div v-if="filteredChats.length === 0" class="empty-state">
        <i class="bi bi-chat-dots"></i>
        <p>{{ searchQuery ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda' }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import whatsappHelper from "@/helpers/whatsapp";
import Str from "@/helpers/str";

export default {
  name: 'ChatList',
  props: {
    chats: {
      type: Array,
      default: () => [],
    },
    selectedChatId: {
      type: Number,
      default: null,
    },
  },
  emits: ['select-chat', 'open-config', 'delete-chat'],
  data() {
    return {
      searchQuery: '',
    };
  },
  computed: {
    filteredChats() {
      if (!this.searchQuery) {
        return this.chats;
      }
      const query = this.searchQuery.toLowerCase();
      return this.chats.filter(chat => {
        const phone = this.formatPhone(chat.phone).toLowerCase();
        const name = (chat.user_name || '').toLowerCase();
        const preview = this.getPreview(chat.last_message).toLowerCase();
        return phone.includes(query) || name.includes(query) || preview.includes(query);
      });
    },
  },
  methods: {
    formatPhone(phone) {
      return whatsappHelper.formatPhone(phone);
    },
    formatTime(date) {
      return whatsappHelper.formatTime(date);
    },
    getPreview(lastMessage) {
      if (!lastMessage) return 'Nenhuma mensagem';
      const content = lastMessage.content || '';
      if (content.length > 50) {
        return content.substring(0, 50) + '...';
      }
      return content;
    },
    selectChat(chatId) {
      this.$emit('select-chat', chatId);
    },
    getPhotoUrl(photo) {
      if (!photo) return null;
      return Str.url(photo);
    },
    handleDelete(chatId) {
      Swal.fire({
        title: 'Tem certeza?',
        text: 'Esta conversa será deletada. Esta ação não pode ser desfeita!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, deletar!',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.$emit('delete-chat', chatId);
        }
      });
    },
  },
}
</script>

<style scoped>
.chat-list-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e4e6eb;
}

.chat-list-header {
  padding: 16px;
  border-bottom: 1px solid #e4e6eb;
  background: #f0f2f5;
}

.chat-list-header h5 {
  color: #780f5a;
  font-weight: 600;
  margin-bottom: 12px;
}

.search-wrapper {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 0.9rem;
  outline: none;
}

.search-input:focus {
  border-color: #780f5a;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.chat-list-content {
  flex: 1;
  overflow-y: auto;
}

.chat-item {
  display: flex;
  align-items: center;
  padding: 0;
  border-bottom: 1px solid #f0f2f5;
  transition: background 0.2s;
  position: relative;
}

.chat-item:hover {
  background: #f5f6f6;
}

.chat-item:hover .chat-delete-btn {
  opacity: 1;
}

.chat-item.active {
  background: #e5ddd5;
}

.chat-item-content {
  display: flex;
  flex: 1;
  padding: 12px;
  cursor: pointer;
  min-width: 0;
}

.chat-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #780f5a 0%, #9d4779 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin-right: 12px;
  flex-shrink: 0;
  overflow: hidden;
}

.chat-avatar.has-photo {
  background: transparent;
  padding: 0;
}

.chat-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.chat-info {
  flex: 1;
  min-width: 0;
}

.chat-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.chat-name {
  font-weight: 600;
  color: #111b21;
  font-size: 0.95rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-time {
  font-size: 0.75rem;
  color: #667781;
  flex-shrink: 0;
  margin-left: 8px;
}

.chat-preview-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.chat-preview {
  font-size: 0.85rem;
  color: #667781;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.unread-badge {
  background: #25d366;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
  margin-left: 8px;
}

.chat-status-row {
  margin-top: 4px;
}

.status-badge {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
}

.ai-badge {
  background: rgba(120, 15, 90, 0.1);
  color: #780f5a;
}

.user-badge {
  background: rgba(37, 211, 102, 0.1);
  color: #25d366;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #667781;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
}

.chat-delete-btn {
  background: transparent;
  border: none;
  color: #dc3545;
  padding: 8px 12px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  margin-right: 8px;
}

.chat-delete-btn:hover {
  background: rgba(220, 53, 69, 0.1);
}

.chat-delete-btn i {
  font-size: 1rem;
}
</style>

